import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import useFetchSites from "../hooks/useFetchSites";
import useUsers from "../hooks/useUsers";

import { useEnums } from "../contexts/EnumContext";

import MapPopup from '../components/MapPopup';

import CustomSelect from "../utils/CustomSelect";
import SectionCard from "../utils/SectionCard";

import placeholderSitePhoto from "../assets/img/placeholder.png";

import TextAreaEditor from "../utils/TextAreaEditor";


const Row = ({ children }) => (
    <div className="gap-6 grid grid-cols-1 lg:grid-cols-2">{children}</div>
);

const Field = ({ label, children, required, mono }) => (
    <label className="block">
        <span className="text-gray-700 text-sm">
            {label} {required && <span className="text-red-500">*</span>}
        </span>
        <div className={`${mono ? "font-mono" : ""} mt-1`}>{children}</div>
    </label>
);

const isZeroDate = (s) => !s || s === "0000-00-00";
const toInputDate = (s) => (isZeroDate(s) ? "" : s?.slice(0, 10) || "");
const fromInputDate = (s) => (s ? s : "0000-00-00");

export default function SiteCreatePage() {
    const navigate = useNavigate();

    const { createSite, loading } = useFetchSites();
    const { users, fetchUsers } = useUsers();

    const { enums } = useEnums();

    useEffect(() => {
        fetchUsers();
    }, []);

    const [mapOpen, setMapOpen] = useState(false);

    console.log("Enums dans SiteCreatePage :", enums);

    const [site, setSite] = useState({
        // Identité
        Nom: "",
        b64_ImageSite: "",
        Type_ImageSite: "",
        Societe: "",
        CA: "",
        Entite: "",
        Centre_Profit: "",
        Type_Ouvrage: "",
        // Chiffre_Affaires: "",

        // Adresse & Localisation
        Adresse1: "",
        Ville: "",
        CP: "",
        Pays: "France",
        Localisation: { latitude: null, longitude: null },
        Code_Chantier: "",
        Code_compta: "",

        // Références opérationnelles
        Operation: "",
        Marche_Num: "",
        Type_Marche: "",
        Lot_Libelle: "",
        Total_Marche: "",
        Nb_Logements_Neuf: 0,
        Nb_Logements_Rea: 0,

        // Parties prenantes
        Me_Ouvrage: "",
        Me_Oeuvre: "",
        Proprietaire: "",

        // Assurance D.O.
        DO_Assureur: "",
        DO_Num_Police: "",

        // Dates clés
        Date_DOC: "0000-00-00",
        Date_PVRecep: "0000-00-00",
        Date_Fin_Garantie: "0000-00-00",
        GPA_Date: "0000-00-00",
        GBF_Fin: "0000-00-00",
        GPA_PriseEnCharge: "0000-00-00",
        GPA_Visite: "0000-00-00",
        Date_Leve_Reserve: "0000-00-00",
        Date_Prev_Trans: "0000-00-00",
        Date_Transfert: "0000-00-00",

        // Financier (récap)
        Total_Pathologie: "",
        Total_A_Payer: "",

        // Archivage & Partage
        Archive_Classeur: "",
        Archive_Situation: "",
        Archive_Salle: "",
        Archive_Rayonnage: "",
        Archive_Etat: "",

        // Divers / textes (tu peux garder si utile ailleurs)
        Reception_Multiple: "",
        Remarques: "",

        // SEP & groupement (gardé si nécessaire ailleurs)
        SEP: false,
        Sep_Nom: "",
        Sep_PC: "",
        Marche_Date: "0000-00-00",
    });

    const [errors, setErrors] = useState({});
    const set = (name, value) => setSite((s) => ({ ...s, [name]: value }));

    const [imgOpen, setImgOpen] = useState(false);

    const imageSrc =
        site?.b64_ImageSite && String(site.b64_ImageSite).trim()
            ? (String(site.b64_ImageSite).startsWith("data:image")
                ? site.b64_ImageSite
                : `data:image/jpeg;base64,${site.b64_ImageSite}`)
            : placeholderSitePhoto;

    const hasRealImage =
        !!(site?.b64_ImageSite && String(site.b64_ImageSite).trim());

    function readFileAsDataURL(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    async function handlePickImage(file) {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            alert("Image trop lourde (>5 Mo).");
            return;
        }
        try {
            const dataUrl = await readFileAsDataURL(file);
            set("b64_ImageSite", dataUrl);
        } catch (e) {
            console.error(e);
            alert("Impossible de lire l’image.");
        }
    }

    function splitDataUrl(input) {
        const m = /^data:(.*?);base64,(.+)$/i.exec(input || "");
        if (m) {
            return { mime: m[1] || "image/jpeg", base64: m[2] };
        }
        return { mime: null, base64: (input || "").trim() };
    }

    function sanitizeBase64(b64) {
        let s = (b64 || "").trim().replace(/\s+/g, "");
        s = s.replace(/ /g, "+").replace(/%2B/gi, "+");
        const pad = s.length % 4;
        if (pad) s += "=".repeat(4 - pad);
        return s;
    }

    const cmpFR = (a = "", b = "") =>
        a.localeCompare(b, "fr", { sensitivity: "base" });

    const usersCAOptions = (users ?? [])
        .filter(u => !!u?.Charge_Affaire)
        .reduce((acc, u) => {
            const key = u?.UUID_ ?? `${u?.Nom}-${u?.Prenom}`;
            if (!acc.map.has(key)) {
                acc.map.set(key, u);
                acc.list.push(u);
            }
            return acc;
        }, { map: new Map(), list: [] }).list
        .sort((a, b) => cmpFR(a?.Nom, b?.Nom) || cmpFR(a?.Prenom, b?.Prenom))
        .map(u => ({
            value: u?.UUID_,
            label: `${u?.Nom ?? ""}`.trim()
        }));

    const validate = () => {
        const e = {};
        if (!site.Nom?.trim()) e.Nom = "Nom requis";
        if (!site.Societe?.trim()) e.Societe = "Société requise";
        if (!site.Entite?.trim()) e.Entite = "Entité requise";
        if (!site.Ville?.trim()) e.Ville = "Ville requise";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSave = async () => {
        if (!validate()) return;

        const payload = {
            ...site,

            b64_ImageSite: site?.b64_ImageSite ? sanitizeBase64(splitDataUrl(site.b64_ImageSite).base64) : "",
            Type_ImageSite: site?.b64_ImageSite ? (splitDataUrl(site.b64_ImageSite).mime || "image/jpg") : "",

            // Dates clés
            Date_DOC: fromInputDate(toInputDate(site.Date_DOC)),
            Date_PVRecep: fromInputDate(toInputDate(site.Date_PVRecep)),
            Date_Fin_Garantie: fromInputDate(toInputDate(site.Date_Fin_Garantie)),
            GPA_Date: fromInputDate(toInputDate(site.GPA_Date)),
            GBF_Fin: fromInputDate(toInputDate(site.GBF_Fin)),
            GPA_PriseEnCharge: fromInputDate(toInputDate(site.GPA_PriseEnCharge)),
            GPA_Visite: fromInputDate(toInputDate(site.GPA_Visite)),
            Date_Leve_Reserve: fromInputDate(toInputDate(site.Date_Leve_Reserve)),
            Date_Prev_Trans: fromInputDate(toInputDate(site.Date_Prev_Trans)),
            Date_Transfert: fromInputDate(toInputDate(site.Date_Transfert)),

            // Marché
            Marche_Date: fromInputDate(toInputDate(site.Marche_Date)),
        };

        const created = await createSite(payload);

        if (created?.UUID_) navigate(`/sites/${created.UUID_}`);
        else navigate(`/sites`);
    };

    // Exemples : remplace par tes vraies listes
    const societeOptions = ["Kletel Multimédia", "SAVPlus", "BGE", "Autre société"];
    const entiteOptions = ["ADAU Alpes-Dauphiné", "IDF Paris", "PACA", "Occitanie"];

    return (
        <div className="mx-auto p-4 lg:p-8">
            {/* Header */}
            <div className="top-4 lg:top-8 z-20 sticky flex justify-center items-center bg-gray-900 drop-shadow-xl mb-6 px-8 rounded-lg w-full min-h-20">
                <div className="flex justify-between items-center px-4 md:px-0 py-3 w-full h-full">
                    <Link to="/sites" className="inline-flex items-center gap-2 text-white hover:text-orange-500 transition duration-300 ease-in-out">
                        <i className="fa-arrow-left fa-solid" />
                        <span>Revenir à la liste des Sites</span>
                    </Link>
                    <button
                        onClick={onSave}
                        disabled={loading}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white 
              ${loading ? "bg-orange-400 cursor-not-allowed" : "bg-orange-600 hover:bg-orange-500 transition duration-300 ease-in-out cursor-pointer"}`}
                    >
                        <i className="fa-solid fa-floppy-disk" />
                        <span>Sauvegarder le site</span>
                    </button>
                </div>
            </div>

            <MapPopup
                isVisible={mapOpen}
                onClose={() => setMapOpen(false)}
                location={site}
                title={`Localisation du Site '${site.Nom}'`}
            />

            {/* Bandeau titre */}
            <div className="flex items-end gap-4 bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 mt-4 p-4 px-6 rounded-lg text-white">
                <p className="space-x-2 font-bold text-2xl">
                    <i className="fa-solid fa-building fa-sm" />
                    <span>{site.Nom || "Création d'un nouveau Site"}</span>
                </p>
                {(site.Adresse1 && site.Ville && site.CP) ? (
                    <p onClick={() => setMapOpen(true)} className="space-x-2 font-medium text-sm text-center hover:underline cursor-pointer">
                        <i className="fa-solid fa-location-dot" />
                        <span>{site.Adresse1}</span>
                        <span className="font-bold">({site.Ville}{site.CP ? `, ${site.CP}` : ""})</span>
                    </p>
                ) : null}
            </div>

            {/* --- NOUVELLE ORGANISATION --- */}
            <div className="space-y-6 mt-6">

                {/* 1) Identité du site */}
                <Row>
                    <SectionCard title="Identité du site" icon="fa-id-card">
                        <div className="gap-4 grid grid-cols-[96px_1fr] sm:grid-cols-[120px_1fr]">
                            {/* Colonne image */}
                            <div className="flex">
                                <div className="group relative">
                                    {/* Bouton carré + dropzone */}
                                    <label
                                        title={hasRealImage ? "Cliquer pour zoomer, ou remplacer" : "Cliquer pour ajouter une image"}
                                        className={[
                                            "relative block w-24 h-24 sm:w-28 sm:h-28 rounded-lg overflow-hidden",
                                            "border border-gray-200 bg-gray-50",
                                            "cursor-pointer"
                                        ].join(" ")}
                                        onClick={(e) => {
                                            // si une image existe et que le clic n’est pas sur le petit bouton “zoom”, on ouvre le file picker
                                            // (le zoom a son propre bouton ci-dessous)
                                        }}
                                        onDragOver={(e) => { e.preventDefault(); }}
                                        onDrop={async (e) => {
                                            e.preventDefault();
                                            const f = e.dataTransfer.files?.[0];
                                            if (f) await handlePickImage(f);
                                        }}
                                    >
                                        {/* Image, toujours contenue dans le carré */}
                                        <img
                                            src={imageSrc}
                                            alt="Photo du site"
                                            className="absolute inset-0 w-full h-full object-contain"
                                            loading="lazy"
                                            onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = placeholderSitePhoto;
                                            }}
                                        />

                                        {/* Overlay d’état (au survol) */}
                                        <div className="hidden absolute inset-0 group-hover:flex justify-center items-end bg-black/0 hover:bg-black/10 transition">
                                            <div className="bg-white/90 mb-1 px-2 py-0.5 rounded-md text-gray-700 text-xs">
                                                {hasRealImage ? "Remplacer l’image" : "Ajouter une image"}
                                            </div>
                                        </div>

                                        {/* Input fichier masqué */}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={async (e) => {
                                                const f = e.target.files?.[0];
                                                if (f) await handlePickImage(f);
                                                e.target.value = "";
                                            }}
                                        />
                                    </label>

                                    {/* Barre d’actions : Zoom + Supprimer (si image) */}
                                    <div className="flex flex-col gap-2 mt-2">
                                        <button
                                            type="button"
                                            disabled={!hasRealImage}
                                            onClick={() => setImgOpen(true)}
                                            className={`inline-flex items-center justify-center rounded-md border px-2.5 py-1 text-xs
              ${hasRealImage
                                                    ? "border-gray-300 text-gray-700 hover:bg-gray-50"
                                                    : "border-gray-200 text-gray-300 cursor-not-allowed"
                                                }`}
                                            title={hasRealImage ? "Agrandir l’image" : "Aucune image à agrandir"}
                                        >
                                            <i className="mr-1 fa-solid fa-magnifying-glass" /> Zoom
                                        </button>

                                        {hasRealImage && (
                                            <button
                                                type="button"
                                                onClick={() => set("b64_ImageSite", "")}
                                                className="inline-flex justify-center items-center hover:bg-red-50 px-2.5 py-1 border border-red-300 rounded-md text-red-600 text-xs"
                                                title="Supprimer l’image"
                                            >
                                                <i className="mr-1 fa-solid fa-trash" /> Supprimer
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Colonne champs */}
                            <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                                <Field label="Nom du site" required>
                                    <input
                                        className={`w-full border rounded px-3 py-2 ${errors.Nom ? "border-red-400" : "border-gray-300"}`}
                                        value={site.Nom}
                                        onChange={(e) => set("Nom", e.target.value)}
                                    />
                                    {errors.Nom && <p className="mt-1 text-red-500 text-xs">{errors.Nom}</p>}
                                </Field>

                                <Field label="Société" required>
                                    <CustomSelect
                                        isPortal
                                        options={enums[`Societes`] ?? []}
                                        value={site.Societe || "Sélectionner…"}
                                        onChange={(val) => set("Societe", typeof val === "string" ? val : val?.label || "")}
                                        className="w-full"
                                    />
                                    {errors.Societe && <p className="mt-1 text-red-500 text-xs">{errors.Societe}</p>}
                                </Field>

                                <Field label="Chargé d’affaires">
                                    <CustomSelect
                                        isPortal
                                        isNested
                                        options={usersCAOptions ?? []}
                                        value={site.CA || "Sélectionner…"}
                                        onChange={(val) => set("CA", typeof val === "string" ? val : val?.label || "")}
                                        className="w-full"
                                    />
                                </Field>

                                <Field label="Entité / Région" required>
                                    <CustomSelect
                                        isPortal
                                        options={enums[`Site_Entité`] ?? []}
                                        value={site.Entite || "Sélectionner…"}
                                        onChange={(val) => set("Entite", typeof val === "string" ? val : val?.label || "")}
                                        className="w-full"
                                    />
                                    {errors.Entite && <p className="mt-1 text-red-500 text-xs">{errors.Entite}</p>}
                                </Field>

                                <Field label="Centre de profit">
                                    <input
                                        className="px-3 py-2 border border-gray-300 rounded w-full"
                                        value={site.Centre_Profit}
                                        onChange={(e) => set("Centre_Profit", e.target.value)}
                                    />
                                </Field>

                                <Field label="Type d’ouvrage">
                                    <CustomSelect
                                        isPortal
                                        options={enums[`Domaine d'activite`] ?? []}
                                        value={site.Type_Ouvrage || "Sélectionner…"}
                                        onChange={(val) => set("Type_Ouvrage", typeof val === "string" ? val : val?.label || "")}
                                        className="w-full"
                                    />
                                </Field>
                            </div>
                        </div>

                        {/* Modal zoom plein écran */}
                        {imgOpen && hasRealImage && (
                            <div
                                role="dialog"
                                aria-modal="true"
                                className="z-[1000] fixed inset-0 flex justify-center items-center bg-black/50 p-4"
                                onClick={() => setImgOpen(false)}
                            >
                                <div
                                    className="relative max-w-[90vw] max-h-[90vh]"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <img
                                        src={imageSrc}
                                        alt="Aperçu"
                                        className="shadow-2xl rounded-xl max-w-[90vw] max-h-[90vh] object-contain"
                                    />
                                    <button
                                        type="button"
                                        aria-label="Fermer"
                                        onClick={() => setImgOpen(false)}
                                        className="inline-flex -top-3 -right-3 absolute justify-center items-center bg-orange-600 hover:bg-orange-500 shadow-lg rounded-full w-9 h-9 text-white transition duration-300"
                                    >
                                        <i className="fa-solid fa-xmark" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </SectionCard>
                    {/* 2) Adresse & Localisation */}
                    <SectionCard title="Adresse & Localisation" icon="fa-location-dot">
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                            <Field label="Adresse complète">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Adresse1}
                                    onChange={(e) => set("Adresse1", e.target.value)}
                                />
                            </Field>

                            <Field label="Code postal / Ville / Pays">
                                <div className="gap-2 grid grid-cols-3">
                                    <input
                                        className="px-3 py-2 border border-gray-300 rounded w-full"
                                        placeholder="CP"
                                        value={site.CP}
                                        onChange={(e) => set("CP", e.target.value)}
                                    />
                                    <input
                                        className={`w-full border rounded px-3 py-2 ${errors.Ville ? "border-red-400" : "border-gray-300"}`}
                                        placeholder="Ville"
                                        value={site.Ville}
                                        onChange={(e) => set("Ville", e.target.value)}
                                    />
                                    <input
                                        className="px-3 py-2 border border-gray-300 rounded w-full"
                                        placeholder="Pays"
                                        value={site.Pays}
                                        onChange={(e) => set("Pays", e.target.value)}
                                    />
                                </div>
                                {errors.Ville && <p className="mt-1 text-red-500 text-xs">{errors.Ville}</p>}
                            </Field>

                            <Field label="Code chantier" mono>
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Code_Chantier}
                                    onChange={(e) => set("Code_Chantier", e.target.value)}
                                />
                            </Field>

                            <Field label="Code compta" mono>
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Code_compta}
                                    onChange={(e) => set("Code_compta", e.target.value)}
                                />
                            </Field>
                        </div>
                    </SectionCard>
                </Row>

                {/* 3) Références opérationnelles */}
                <Row>
                    <SectionCard title="Références opérationnelles" icon="fa-hashtag">
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                            <Field label="Opération">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Operation}
                                    onChange={(e) => set("Operation", e.target.value)}
                                />
                            </Field>

                            <Field label="Numéro de marché" mono>
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Marche_Num}
                                    onChange={(e) => set("Marche_Num", e.target.value)}
                                />
                            </Field>

                            <Field label="Type de marché">
                                <CustomSelect
                                    isPortal
                                    options={enums[`Type de Marche`] ?? []}
                                    value={site.Type_Marche || "Sélectionner…"}
                                    onChange={(val) => set("Type_Marche", typeof val === "string" ? val : val?.label || "")}
                                    className="w-full"
                                />
                            </Field>

                            <Field label="Lots">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Lot_Libelle}
                                    onChange={(e) => set("Lot_Libelle", e.target.value)}
                                />
                            </Field>

                            <Field label="Montant du marché (€)">
                                <input
                                    type="number"
                                    step="0.01"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Total_Marche}
                                    onChange={(e) => set("Total_Marche", e.target.value)}
                                />
                            </Field>

                            <div className="gap-4 grid grid-cols-2 sm:col-span-2">
                                <Field label="Nombre de logements (neuf)">
                                    <input
                                        type="number"
                                        className="px-3 py-2 border border-gray-300 rounded w-full"
                                        value={site.Nb_Logements_Neuf}
                                        onChange={(e) => set("Nb_Logements_Neuf", Number(e.target.value || 0))}
                                    />
                                </Field>
                                <Field label="Nombre de logements (réhabilitation)">
                                    <input
                                        type="number"
                                        className="px-3 py-2 border border-gray-300 rounded w-full"
                                        value={site.Nb_Logements_Rea}
                                        onChange={(e) => set("Nb_Logements_Rea", Number(e.target.value || 0))}
                                    />
                                </Field>
                            </div>
                        </div>
                    </SectionCard>


                    <SectionCard title="Dates clés" icon="fa-calendar-days">
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                            <Field label="Date DOC">
                                <input
                                    type="date"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(site.Date_DOC)}
                                    onChange={(e) => set("Date_DOC", fromInputDate(e.target.value))}
                                />
                            </Field>
                            <Field label="PV de réception">
                                <input
                                    type="date"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(site.Date_PVRecep)}
                                    onChange={(e) => set("Date_PVRecep", fromInputDate(e.target.value))}
                                />
                            </Field>
                            <Field label="Fin de garantie">
                                <input
                                    type="date"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(site.Date_Fin_Garantie)}
                                    onChange={(e) => set("Date_Fin_Garantie", fromInputDate(e.target.value))}
                                />
                            </Field>
                            <Field label="GPA prise en charge">
                                <input
                                    type="date"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(site.GPA_PriseEnCharge)}
                                    onChange={(e) => set("GPA_PriseEnCharge", fromInputDate(e.target.value))}
                                />
                            </Field>
                            <Field label="Fin GPA">
                                <input
                                    type="date"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(site.GPA_Date)}
                                    onChange={(e) => set("GPA_Date", fromInputDate(e.target.value))}
                                />
                            </Field>
                            <Field label="Fin GBF">
                                <input
                                    type="date"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(site.GBF_Fin)}
                                    onChange={(e) => set("GBF_Fin", fromInputDate(e.target.value))}
                                />
                            </Field>
                            <Field label="GPA Visite">
                                <input
                                    type="date"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(site.GPA_Visite)}
                                    onChange={(e) => set("GPA_Visite", fromInputDate(e.target.value))}
                                />
                            </Field>
                            <Field label="PV levée de réserves">
                                <input
                                    type="date"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(site.Date_Leve_Reserve)}
                                    onChange={(e) => set("Date_Leve_Reserve", fromInputDate(e.target.value))}
                                />
                            </Field>
                            <Field label="Pré-transfert">
                                <input
                                    type="date"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(site.Date_Prev_Trans)}
                                    onChange={(e) => set("Date_Prev_Trans", fromInputDate(e.target.value))}
                                />
                            </Field>
                            <Field label="Transfert">
                                <input
                                    type="date"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(site.Date_Transfert)}
                                    onChange={(e) => set("Date_Transfert", fromInputDate(e.target.value))}
                                />
                            </Field>
                        </div>
                    </SectionCard>

                </Row>

                {/* 5) Assurance D.O. & 6) Dates clés */}
                <Row>
                    <SectionCard title="Assurance D.O." icon="fa-shield-halved">
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                            <Field label="Nom de l’assureur">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.DO_Assureur}
                                    onChange={(e) => set("DO_Assureur", e.target.value)}
                                />
                            </Field>
                            <Field label="Numéro de police D.O." mono>
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.DO_Num_Police}
                                    onChange={(e) => set("DO_Num_Police", e.target.value)}
                                />
                            </Field>
                        </div>
                    </SectionCard>

                    {/* 4) Parties prenantes */}
                    <SectionCard title="Parties prenantes" icon="fa-people-group">
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                            <Field label="Maître d’ouvrage">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Me_Ouvrage}
                                    onChange={(e) => set("Me_Ouvrage", e.target.value)}
                                />
                            </Field>
                            <Field label="Maître d’œuvre">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Me_Oeuvre}
                                    onChange={(e) => set("Me_Oeuvre", e.target.value)}
                                />
                            </Field>
                            <Field label="Propriétaire">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Proprietaire}
                                    onChange={(e) => set("Proprietaire", e.target.value)}
                                />
                            </Field>
                        </div>
                    </SectionCard>

                </Row>

                {/* 7) Financier & 8) Archivage & Partage */}
                <Row>
                    <SectionCard title="Financier" icon="fa-scale-balanced">
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                            <Field label="Coût des pathologies (€)">
                                <input
                                    type="number"
                                    step="0.01"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Total_Pathologie}
                                    onChange={(e) => set("Total_Pathologie", e.target.value)}
                                />
                            </Field>
                            <Field label="Coût des déclarations (€)">
                                <input
                                    type="number"
                                    step="0.01"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Total_A_Payer}
                                    onChange={(e) => set("Total_A_Payer", e.target.value)}
                                />
                            </Field>
                        </div>
                    </SectionCard>

                    <SectionCard title="Archivage & Partage" icon="fa-box-archive">
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-3">
                            <Field label="Classeur">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Archive_Classeur}
                                    onChange={(e) => set("Archive_Classeur", e.target.value)}
                                />
                            </Field>
                            <Field label="Situation">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Archive_Situation}
                                    onChange={(e) => set("Archive_Situation", e.target.value)}
                                />
                            </Field>
                            <Field label="Salle">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Archive_Salle}
                                    onChange={(e) => set("Archive_Salle", e.target.value)}
                                />
                            </Field>
                            <Field label="Rayonnage">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Archive_Rayonnage}
                                    onChange={(e) => set("Archive_Rayonnage", e.target.value)}
                                />
                            </Field>
                            <Field label="État du dossier">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={site.Archive_Etat}
                                    onChange={(e) => set("Archive_Etat", e.target.value)}
                                />
                            </Field>
                        </div>
                    </SectionCard>
                </Row>

                {/* (Optionnel) Divers / Réserves si tu veux les conserver */}
                <SectionCard title="Divers / Réserves" icon="fa-file-lines">
                    <div className="gap-4 grid grid-cols-1">
                        <Field label="Réceptions / Réserves multiples">
                            <textarea
                                rows={6}
                                className="px-3 py-2 border border-gray-300 rounded w-full"
                                value={site.Reception_Multiple}
                                onChange={(e) => set("Reception_Multiple", e.target.value)}
                                placeholder="..."
                            />
                        </Field>
                    </div>
                </SectionCard>

                {/* (Optionnel) Remarques */}
                <SectionCard title="Remarques" icon="fa-file-lines">
                    <div className="text-gray-700 text-sm py-1">Remarques</div>
                    <TextAreaEditor
                        value={site.Remarques}
                        onChange={(e) => set("Remarques", e.target.value)}
                        placeholder="Saisir les Remarques"
                    />
                </SectionCard>
            </div>
        </div>
    );
}
